import React, { useState, useEffect, useRef } from "react";
import { Page } from 'components/common';
import TaskBucket from "../User/TaskBucket";
import styles from "assets/scss/task_management.module.css";
import "assets/scss/style.css";
import { NotificationManager } from "react-notifications";
import { PayrollClient } from "services";
import { useOrganisationContext, useUserContext } from "hooks";
import AssignedTaskCard from "../Employee/AssignedTaskCard";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import TeamProgress from "./TeamProgress";
import { Trans } from "react-i18next";
import TaskChart from "./TasksChart";
import { Button, Nav } from "reactstrap";
import { ColouredLink } from "components/HoverCard";

const ManagerTasks = ({t}) => {
    const [buckets, setBuckets] = useState([]);
    const [newBucketName, setNewBucketName] = useState("");
    const [showNewBucketInput, setShowNewBucketInput] = useState(false);
    
    const [searchQuery, setSearchQuery] = useState("");
    const [employees, setEmployees] = useState([]);
    const [groups, setGroups] = useState([]);
    const [taskStatuses, setTaskStatuses] = useState([null]);
    const [labels, setLabels] = useState(null);
    const [assignedTasks, setAssignedTasks] = useState([]);
    const [teamProgress, setTeamProgress] = useState({ assigned: [], inProgress: [], completed: [] });
    const [showOptions, setShowOptions] = useState({dropdown: false, id: ''});

    const client = PayrollClient();
    const { selectedOrganisation } = useOrganisationContext();
    const { user } = useUserContext();

    const hideAddTaskInputRef = useRef(null);
    
    const userPermission = user?.permissions?.includes("hr_task_manager");
    
    const [activeTab, setActiveTab] = useState(!userPermission ? 'myTasks' : 'teamTasks');

    const handleClickOutside = (event) => {
        if (hideAddTaskInputRef.current && !hideAddTaskInputRef.current.contains(event.target)) {
            setShowNewBucketInput(false);
        }
    };

    useEffect(() => {
        if (showNewBucketInput) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showNewBucketInput]);

    const fetchData = async () => {
        try {
            const response = await client.get(`/planner-bucket-list`, {
                params: {
                    my_task: activeTab === 'myTasks'
                }
            });
            const data = response.data;
            setBuckets(data);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const reassignTask = async (taskId, employees, reason) => {
        try {
            await client.post(`/reassign-task/${taskId}/reassign_task`, {
                task: taskId,
                employees: employees,
                reason: reason,
            });
            NotificationManager.success(t("Re-assign request sent successfully"));
            fetchData();
            reassignTaskList();
        } catch (error) {
            console.error('Error reassigning task:', error);
            NotificationManager.error(t("Failed to reassign task"));
        }
    };

    const reassignTaskList = async () => {
        try {
            const response = await client.get(`/reassign-task`);
            const data = response.data;
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await client.get(`/employees-list-for-task`);
            const data = response.data;
            setEmployees(data);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const fetchGroups = async () => {
        try {
            const response = await client.get(`/group-employees-management/list-of-groups`);
            const data = response;
            setGroups(data.data);
        } catch (error) {
            console.error("Error fetching groups:", error);
        }
    };

    const fetchLabels = async () => {
        try {
            const response = await client.get(`/employee-bucket-task-label-management`);
            const data = response;
            setLabels(data.data);
        } catch (error) {
            console.error("Error fetching labels:", error);
        }
    };

    const fetchAssignedTasks = async () => {
        try {
            const response = await client.get(`/re-assigned-tasks-list`);
            const data = response;
            setAssignedTasks(data.data);
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    const fetchTeamProgress  = async () => {
        try {
    
                const [assignedResponse, inProgressResponse, completedResponse] = await Promise.all([
                    client.get('/re-assigned-tasks-list/team_progress_assigned_tasks'),
                    client.get(`/bucket-task-management/team_progress_in_progress`),
                    client.get(`/bucket-task-management/team_progress_completed`)
                ]);

                setTeamProgress({
                    assigned: assignedResponse.data,
                    inProgress: inProgressResponse.data,
                    completed: completedResponse.data,
                });
        } catch (error) {
            console.error('Error fetching tasks:', error);
        }
    }

    const fetchAllTasks = async () => {
        if (activeTab === "progress") {
            fetchTeamProgress()
        } else if (activeTab === "myTasks" || activeTab === "teamTasks") {
            fetchData();
            fetchEmployees();
            fetchLabels();
            fetchGroups();
        } else if (activeTab === "requests") {
            fetchEmployees();
            reassignTaskList();
        } else if (activeTab === "assignedTasks") {
            fetchAssignedTasks();
        } else if (activeTab === "tasksChart") {
            fetchTasksForStatus();
        }
    };

    useEffect(() => {

        fetchAllTasks();
        
    }, [selectedOrganisation, activeTab]);

    const addPlannerBucket = async () => {
        try {
            const payload = { bucket_name: newBucketName };
            if (activeTab === 'myTasks') {
                payload.my_task = true;
            }
            await client.post('/planner-bucket-list', payload);
            NotificationManager.success(t("Bucket Added successfully"));
        } catch (err) {
            NotificationManager.error(t('Error while Adding the bucket'));
        }
        fetchData();
    };

    const handleAddNewBucket = () => {
        setShowNewBucketInput(true);
        setTimeout(() => {
            const element = document.getElementById("scrollableContainer");
            if (element) {
                element.style.transition = "scroll-left 0.5s ease";
                element.scrollLeft = element.scrollWidth;
            }
        }, 100);
    
    };

    const handleCreateBucket = () => {
        addPlannerBucket();
        if (newBucketName.trim() !== "") {
            setBuckets([...buckets, { bucket_name: newBucketName.trim(), planner_bucket: [] }]);
            setNewBucketName("");
            setShowNewBucketInput(false);
        }
    };

    // const handleInputBlur = () => {
    //     if (newBucketName.trim() !== "") {
    //         handleCreateBucket();
    //     } else {
    //         setShowNewBucketInput(false);
    //     }
    // };

    const handleDeleteTask = async (task) => {
        try {
            await client.delete(`/bucket-task-management/${task?.id}`);
            NotificationManager.success(t("Task deleted successfully"));
            fetchData();
        } catch (error) {
            console.error('Error deleting task:', error);
            NotificationManager.error(t("Failed to delete task"));
        }
    };

    const updateBucketTask = async (task, activeTab) => {
        try {
            const payload = { ...task };
            if (activeTab === 'myTasks') {
                payload.my_task = true;
            } else if (activeTab === 'assignedTasks'){
                payload.assignedTasks = true;
            }
            await client.put(`/bucket-task-management/${task.id}/update_bucket_task`, payload);
            NotificationManager.success(t("Task Updated successfully"));
            fetchData();
        } catch (err) {
            NotificationManager.error(t('Error while Updating the task'));
        }
    };

    const handleCheckboxChange = async (task, e) => {
        const newProgress = e.target.checked ? "Completed" : "Not Started";
        
        const updatedTask = {
            ...task,
            progress: newProgress,
        };

        await updateBucketTask(updatedTask, activeTab);
    };


    const updateAssignedTask = async (task) => {
        try {
            await client.put(`/re-assigned-tasks-list/${task.id}/update_assigned_task`, task);
            NotificationManager.success(t("Task Updated successfully"));
            fetchData();
        } catch (err) {
            NotificationManager.error(t('Error while Updating the task'));
        }
    };


    const handleDragEnd = async (result) => {
        if (!result.destination) {
            return;
        }
        const { source, destination, draggableId, type } = result;

        if (type === 'BUCKET') {
            const reorderedBuckets = Array.from(buckets);
            const [removed] = reorderedBuckets.splice(source.index, 1);
            reorderedBuckets.splice(destination.index, 0, removed);
            setBuckets(reorderedBuckets);
    
            const orderedBucketIds = reorderedBuckets.map(bucket => bucket.id);
            await update_bucket_order(orderedBucketIds, draggableId);
    
            return;
        };
    
        // Moving tasks within the same bucket
        if (source.droppableId === destination.droppableId) {
            const bucketIndex = buckets.findIndex(bucket => bucket?.id === parseInt(source.droppableId));
            const updatedTasks = Array.from(buckets[bucketIndex]?.planner_bucket);
            const [removed] = updatedTasks.splice(source.index, 1);
            updatedTasks.splice(destination.index, 0, removed);
    
            const updatedBuckets = [...buckets];
            updatedBuckets[bucketIndex].planner_bucket = updatedTasks;
            setBuckets(updatedBuckets);
    
            const orderedTaskIds = updatedTasks.map(task => task.id);
            await updateTaskOrder(orderedTaskIds, draggableId, buckets[bucketIndex].id);
        } else {
            const sourceBucketIndex = buckets.findIndex(bucket => bucket?.id === parseInt(source.droppableId));
            const destinationBucketIndex = buckets.findIndex(bucket => bucket?.id === parseInt(destination.droppableId));

    
            if (destinationBucketIndex === -1) {
                return;
            }
    
            const sourceTasks = Array.from(buckets[sourceBucketIndex]?.planner_bucket);
            const destinationTasks = Array.from(buckets[destinationBucketIndex]?.planner_bucket);
    
            const [removed] = sourceTasks.splice(source.index, 1);
            removed.planner_bucket = buckets[destinationBucketIndex].id;
    
            destinationTasks.splice(destination.index, 0, removed);
    
            setBuckets(prevBuckets => {
                const updatedBuckets = [...prevBuckets];
                updatedBuckets[sourceBucketIndex].planner_bucket = sourceTasks;
                updatedBuckets[destinationBucketIndex].planner_bucket = destinationTasks;
                return updatedBuckets;
            });
    
            const orderedTaskIds = destinationTasks.map(task => task.id);
            await updateTaskOrder(orderedTaskIds, draggableId, buckets[destinationBucketIndex].id);
        }
    };


    const update_bucket_order = async (bucket_ids) => {
        try {
            const response = await client.put(`/planner-bucket-list/update_bucket_order`, { ordered_bucket_ids: bucket_ids });

            if (!response.ok) {
                throw new Error(t('Failed to update bucket order'));
            }


            fetchData();
        } catch (error) {
            console.error('Error updating bucket order:', error);
        }
    }
    
    
    const updateTaskOrder = async (taskIds, draggableId, newBucketId) => {
        try {
            await client.put(`/bucket-task-management/${draggableId}/update_task_order`, { ordered_task_ids: taskIds, new_bucket_id: newBucketId });
            fetchData();
        } catch (err) {
            NotificationManager.error(t("Error while updating task order"));
            console.error('Error updating task order:', err);
        }
    };

    const fetchTasksForStatus = async () => {
        try {
            const response = await client.get(`/bucket-task-management/list_of_tasks_for_status`);
            const data = response.data;
            setTaskStatuses(data);
        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };
    const renderAssignedBucketTasks = () => (
        <div className={`${styles.bucketContainer} ${styles.assignedbucketContainer}`}>
            {assignedTasks.map((bucket, index) => (
                <div key={index} className={styles.bucketRow}>
                    <div className={styles.bucketContent}>
                        <AssignedTaskCard
                            key={index}
                            task={bucket}
                            setBuckets={setBuckets}
                            fetchData={fetchData}
                            fetchEmployees={employees}
                            handleDeleteTask={handleDeleteTask}
                            onUpdate={updateAssignedTask}
                            onReassign={reassignTask}
                            fetchLabels={labels}
                            groups={groups}
                            buckets={buckets}
                            userPermission={userPermission}
                            t={t}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
    const navTab = {
        style: {
          style: { cursor: 'pointer', fontSize: '1.05em', padding: 10 },
        },
        numbers: {
          style: { fontSize: '0.8em' },
        }
      };

    const renderTasksCharts = () => (
        <TaskChart
            fetchData={fetchData}
            tasks={taskStatuses}
            fetchEmployees={employees}
            userPermission={userPermission}
            t={t}
        />
    );

    const renderTeamProgress = () => (
        <div className={`${styles.bucketContainer} ${styles.assignedbucketContainer}`}>
                <div className={styles.bucketRow}>
                    <div className={styles.bucketContent}>
                        <TeamProgress
                            fetchData={fetchData}
                            tasks={teamProgress}
                            fetchEmployees={employees}
                            fetchLabels={labels}
                            groups={groups}
                            buckets={buckets}
                            userPermission={userPermission}
                            t={t}
                        />
                    </div>
                </div>
        </div>
    );

    const filteredBuckets = buckets.map(bucket => {
        const filteredTasks = bucket.planner_bucket.filter(task => 
            task.bucket_task_name?.toLowerCase().includes(searchQuery.toLowerCase())
        );

        if (filteredTasks.length > 0) {
            return {
                ...bucket,
                planner_bucket: filteredTasks
            };
        }
    
        return null;
    }).filter(bucket => bucket !== null);
    
    const bucketsToRender = searchQuery ? filteredBuckets : buckets;


    const renderBuckets = () => (
        <div className={styles.bucketContainer} >
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="board" type="BUCKET" direction="horizontal">
                        {(provided) => (
                            <div className={styles.bucketContainer} ref={provided.innerRef} {...provided.droppableProps} id="scrollableContainer">
                                {bucketsToRender.map((bucket, bucketIndex) => (
                                    <Draggable key={bucket.id} draggableId={`bucket-${bucket.id}`} index={bucketIndex}>
                                        {(provided, snapshot) => (
                                                <div
                                                    className={`${styles.bucketRow} ${snapshot.isDragging ? styles.dragging : ''}`}
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                <div className={styles.bucketContent}>
                                                    <TaskBucket
                                                        key={bucket.id}
                                                        bucket={bucket}
                                                        tasks={bucket.planner_bucket}
                                                        setBuckets={setBuckets}
                                                        fetchData={fetchData}
                                                        fetchEmployees={employees}
                                                        handleDeleteTask={handleDeleteTask}
                                                        onUpdate={updateBucketTask}
                                                        onReassign={reassignTask}
                                                        handleCheckboxChange={handleCheckboxChange}
                                                        fetchLabels={labels}
                                                        groups={groups}
                                                        buckets={buckets}
                                                        setShowOptions={setShowOptions}
                                                        showOptions={showOptions}
                                                        userPermission={userPermission}
                                                        activeTab={activeTab}
                                                        t={t}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                <div className={`d-flex justify-content-center`}  ref={hideAddTaskInputRef}>
                                    {showNewBucketInput ? (
                                        <>
                                        <div className={` `}>
                                            <input
                                                type="text"
                                                value={newBucketName}
                                                onChange={(e) => setNewBucketName(e.target.value)}
                                                placeholder={t("Enter Bucket Name")}
                                                className={styles.newBucketInput}
                                            />
                                        </div>
                                        <div>
                                            <Button color="primary" onClick={handleCreateBucket}><Trans>Add</Trans></Button>
                                        </div>
                                        </>
                                    ) : (
                                        <div><ColouredLink to="#" onClick={handleAddNewBucket}><span className="text-nowrap"><Trans>Add New Bucket</Trans></span></ColouredLink></div>
                                    )}
                                </div>
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
    );

    

    return (
        <Page title={t("Task Management")} className='inv-tabs nw-list'>
            <div>
                <Nav tabs className="nw-tabs2 inv-tabs">
                    {!userPermission && (
                        <div {...navTab.style} onClick={() => setActiveTab('assignedTasks')} className={activeTab == 'assignedTasks' ? `active-tabNum` : ``}>
                            <Trans>Assigned Tasks</Trans>
                        </div>
                    )}
                    {userPermission && (
                        <div {...navTab.style} onClick={() => setActiveTab('teamTasks')} className={activeTab == 'teamTasks' ? `active-tabNum` : ``}>
                            <Trans>Team Tasks</Trans>
                        </div>
                    )}
                    <div {...navTab.style} onClick={() => setActiveTab('myTasks')} className={activeTab == 'myTasks' ? `active-tabNum` : ``}>
                        <Trans>My Tasks</Trans>
                    </div>
                    {userPermission && (
                        <div {...navTab.style} onClick={() => setActiveTab('progress')} className={activeTab == 'progress' ? `active-tabNum` : ``}>
                            <Trans>Team Progress</Trans>
                        </div>
                    )}
                    {userPermission && (
                        <div {...navTab.style} onClick={() => setActiveTab('tasksChart')} className={activeTab == 'tasksChart' ? `active-tabNum` : ``}>
                            <Trans>Task Status</Trans>
                        </div>
                    )}
                </Nav>
            </div>
            <div className={` ${styles.groupHeader} mt-2`}>
                <input 
                    className={styles.searchBar}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("Search tasks by name...")}
                />
            </div>
            {activeTab === 'assignedTasks' && !userPermission && renderAssignedBucketTasks()}
            {activeTab === 'myTasks' && !userPermission && renderBuckets()}
            {activeTab === 'teamTasks' && userPermission && renderBuckets()}
            {activeTab === 'myTasks' && userPermission && renderBuckets()}
            {activeTab === 'progress' && userPermission && renderTeamProgress()}
            {activeTab === 'tasksChart' && userPermission && renderTasksCharts()}
            
        </Page>
    );
};

export default ManagerTasks;
